import { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaCheckCircle, FaCamera, FaSpinner } from 'react-icons/fa';
import { getStorage } from 'firebase/storage';
import { extractTextFromImage, generateSentencesFromText } from '../../../utilities/extensions/imageToText';
import { db } from '../../../firebase';

const FillInBlankMcqActivity = ({ initialContent, onContentChange }) => {
  const [content, setContent] = useState({
    items: [
      {
        sentence: '',
        options: ['', '', '', ''],
        correctOption: ''
      }
    ]
  });

  const [showImagePopup, setShowImagePopup] = useState(false);
  const [targetItemIndex, setTargetItemIndex] = useState(null);
  const [imageProcessing, setImageProcessing] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [generatedOptions, setGeneratedOptions] = useState([]);

  // Activity-level image-to-text for generating multiple sentences
  const [showSentenceImagePopup, setShowSentenceImagePopup] = useState(false);
  const [sentenceImageProcessing, setSentenceImageProcessing] = useState(false);
  const [sentenceUploadedImage, setSentenceUploadedImage] = useState(null);
  const [sentenceImageBlobUrl, setSentenceImageBlobUrl] = useState(null);
  const [generatedSentences, setGeneratedSentences] = useState([]);

  useEffect(() => {
    if (initialContent) {
      try {
        const parsed = JSON.parse(initialContent);
        if (parsed && Array.isArray(parsed.items)) {
          setContent(parsed);
        }
      } catch {
        // keep default content on parse error
      }
    }
  }, [initialContent]);

  useEffect(() => {
    onContentChange(content);
  }, [content, onContentChange]);

  // Activity-level image popup helpers
  const openSentenceImagePopup = () => {
    setShowSentenceImagePopup(true);
    setSentenceUploadedImage(null);
    if (sentenceImageBlobUrl) {
      URL.revokeObjectURL(sentenceImageBlobUrl);
    }
    setSentenceImageBlobUrl(null);
    setGeneratedSentences([]);
    setSentenceImageProcessing(false);
  };

  const closeSentenceImagePopup = () => {
    setShowSentenceImagePopup(false);
    setSentenceUploadedImage(null);
    if (sentenceImageBlobUrl) {
      URL.revokeObjectURL(sentenceImageBlobUrl);
    }
    setSentenceImageBlobUrl(null);
    setGeneratedSentences([]);
    setSentenceImageProcessing(false);
  };

  const handleSentenceImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSentenceUploadedImage(file);
    const blobUrl = URL.createObjectURL(file);
    setSentenceImageBlobUrl(blobUrl);
    setSentenceImageProcessing(true);
    setGeneratedSentences([]);

    try {
      const storage = getStorage();
      const text = await extractTextFromImage(file, storage, db);

      // Use shared helper to turn raw OCR into sentence objects
      const sentences = generateSentencesFromText(text) || [];

      const mapped = sentences
        .map((s) => (s.languageText || s.englishText || '').trim())
        .filter(Boolean);

      setGeneratedSentences(mapped);
    } catch (error) {
      alert('Failed to extract sentences from image: ' + error.message);
    } finally {
      setSentenceImageProcessing(false);
    }
  };

  const applyGeneratedSentences = () => {
    if (!generatedSentences || generatedSentences.length === 0) {
      closeSentenceImagePopup();
      return;
    }

    const newItemsFromImage = generatedSentences.map(sentenceText => ({
      sentence: sentenceText,
      options: ['', '', '', ''],
      correctOption: ''
    }));

    setContent(prev => ({
      ...prev,
      items: [...prev.items, ...newItemsFromImage]
    }));

    closeSentenceImagePopup();
  };

  const addItem = () => {
    const newItems = [
      ...content.items,
      {
        sentence: '',
        options: ['', '', '', ''],
        correctOption: ''
      }
    ];
    setContent(prev => ({ ...prev, items: newItems }));
  };

  const removeItem = (index) => {
    if (content.items.length <= 1) return;
    const newItems = content.items.filter((_, i) => i !== index);
    setContent(prev => ({ ...prev, items: newItems }));
  };

  const updateItemField = (index, field, value) => {
    const newItems = [...content.items];
    newItems[index] = { ...newItems[index], [field]: value };

    // If we changed an option and it was the correct one, keep correctOption
    if (field === 'options') {
      const current = newItems[index];
      if (!current.options.includes(current.correctOption)) {
        current.correctOption = '';
      }
    }

    setContent(prev => ({ ...prev, items: newItems }));
  };

  const updateOption = (itemIndex, optionIndex, value) => {
    const newItems = [...content.items];
    const item = { ...newItems[itemIndex] };
    const newOptions = [...item.options];
    const oldValue = newOptions[optionIndex];
    newOptions[optionIndex] = value;
    item.options = newOptions;

    // If this option was the correct one, update correctOption to new value
    if (item.correctOption === oldValue) {
      item.correctOption = value;
    }

    // If correctOption no longer matches any non-empty option, clear it
    if (!item.options.includes(item.correctOption)) {
      item.correctOption = '';
    }

    newItems[itemIndex] = item;
    setContent(prev => ({ ...prev, items: newItems }));
  };

  const openImagePopupForItem = (index) => {
    setTargetItemIndex(index);
    setShowImagePopup(true);
    setUploadedImage(null);
    if (imageBlobUrl) {
      URL.revokeObjectURL(imageBlobUrl);
    }
    setImageBlobUrl(null);
    setGeneratedOptions([]);
    setImageProcessing(false);
  };

  const closeImagePopup = () => {
    setShowImagePopup(false);
    setTargetItemIndex(null);
    setUploadedImage(null);
    if (imageBlobUrl) {
      URL.revokeObjectURL(imageBlobUrl);
    }
    setImageBlobUrl(null);
    setGeneratedOptions([]);
    setImageProcessing(false);
  };

  const handleImageUploadForOptions = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedImage(file);
    const blobUrl = URL.createObjectURL(file);
    setImageBlobUrl(blobUrl);
    setImageProcessing(true);
    setGeneratedOptions([]);

    try {
      const storage = getStorage();
      const text = await extractTextFromImage(file, storage, db);

      // Split extracted text into candidate options by line breaks and punctuation
      const rawParts = text
        .split(/\r?\n|[.,;:]/)
        .map(t => t.trim())
        .filter(Boolean);

      const uniqueParts = Array.from(new Set(rawParts));

      setGeneratedOptions(uniqueParts.slice(0, 8));
    } catch (error) {
      alert('Failed to extract text from image: ' + error.message);
    } finally {
      setImageProcessing(false);
    }
  };

  const applyGeneratedOptionsToItem = () => {
    if (targetItemIndex == null || generatedOptions.length === 0) {
      closeImagePopup();
      return;
    }

    const newItems = [...content.items];
    const item = { ...newItems[targetItemIndex] };

    const optionsFromImage = generatedOptions
      .map(opt => opt.trim())
      .filter(Boolean)
      .slice(0, 4);

    if (optionsFromImage.length === 0) {
      closeImagePopup();
      return;
    }

    // Fill options array with generated options (up to 4)
    const newOptions = [...item.options];
    for (let i = 0; i < 4; i++) {
      newOptions[i] = optionsFromImage[i] || '';
    }
    item.options = newOptions;

    // Default correct option to the first generated option
    item.correctOption = optionsFromImage[0];

    newItems[targetItemIndex] = item;
    setContent(prev => ({ ...prev, items: newItems }));
    closeImagePopup();
  };

  const setCorrectOption = (itemIndex, optionValue) => {
    const newItems = [...content.items];
    const item = { ...newItems[itemIndex] };
    item.correctOption = optionValue;
    newItems[itemIndex] = item;
    setContent(prev => ({ ...prev, items: newItems }));
  };

  const validate = () => {
    const errors = [];

    if (!content.items || content.items.length === 0) {
      errors.push('At least one sentence is required');
      return errors;
    }

    content.items.forEach((item, index) => {
      const prefix = `Item ${index + 1}:`;

      if (!item.sentence || !item.sentence.trim()) {
        errors.push(`${prefix} Sentence is required`);
      }

      if (!item.sentence?.includes('_______')) {
        errors.push(`${prefix} Sentence must contain a blank marked as _______`);
      }

      if (!item.options || !Array.isArray(item.options) || item.options.length === 0) {
        errors.push(`${prefix} At least one option is required`);
      } else {
        const filledOptions = item.options.filter(opt => opt && opt.trim());
        if (filledOptions.length < 2) {
          errors.push(`${prefix} At least two non-empty options are required`);
        }

        if (!item.correctOption || !filledOptions.includes(item.correctOption)) {
          errors.push(`${prefix} Correct answer must be one of the non-empty options`);
        }
      }
    });

    return errors;
  };

  const errors = validate();

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">Fill in the Blanks Items</h3>
            <p className="text-sm text-gray-600 mt-1">{content.items.length} item{content.items.length !== 1 ? 's' : ''} configured</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium shadow-sm"
              onClick={openSentenceImagePopup}
            >
              <FaCamera className="w-4 h-4 mr-2" />
              Generate from Image
            </button>
            <button
              type="button"
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm"
              onClick={addItem}
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Add Sentence
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {content.items.map((item, index) => {
            const itemErrors = errors.filter(e => e.startsWith(`Item ${index + 1}:`));

            return (
              <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start gap-4">
                  {/* Index */}
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0 mt-1">
                    {index + 1}
                  </div>

                  <div className="flex-1 space-y-4">
                    {/* Sentence */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sentence (use ______ for the blank) *
                      </label>
                      <textarea
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                        rows={2}
                        placeholder="e.g. Raju ______ to school."
                        value={item.sentence || ''}
                        onChange={(e) => updateItemField(index, 'sentence', e.target.value)}
                      />
                      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                        <span>Tip: Use exactly 7 underscores (_______) where the blank should appear.</span>
                        <button
                          type="button"
                          className="inline-flex items-center px-2 py-1 rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                          onClick={() => openImagePopupForItem(index)}
                        >
                          <FaCamera className="w-3 h-3 mr-1" />
                          Generate options from image
                        </button>
                      </div>
                    </div>

                    {/* Options */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Options (click the check icon to mark the correct answer)
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {item.options.map((opt, optIndex) => {
                          const isCorrect = item.correctOption === opt && opt && opt.trim();
                          return (
                            <div key={optIndex} className="flex items-center gap-2">
                              <input
                                type="text"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm"
                                placeholder={`Option ${optIndex + 1}`}
                                value={opt || ''}
                                onChange={(e) => updateOption(index, optIndex, e.target.value)}
                              />
                              <button
                                type="button"
                                className={`p-2 rounded-lg border text-sm flex-shrink-0 transition-colors ${
                                  isCorrect
                                    ? 'bg-green-100 border-green-400 text-green-700'
                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-green-50 hover:border-green-300 hover:text-green-700'
                                }`}
                                onClick={() => {
                                  const value = item.options[optIndex];
                                  if (value && value.trim()) {
                                    setCorrectOption(index, value);
                                  }
                                }}
                                title="Mark as correct answer"
                              >
                                <FaCheckCircle className="w-4 h-4" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Row actions and errors */}
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs text-gray-500">
                        Tip: Make sure exactly one of the filled options is marked as correct.
                      </div>
                      {content.items.length > 1 && (
                        <button
                          type="button"
                          className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0"
                          onClick={() => removeItem(index)}
                          title="Remove Sentence"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {/* Item-level validation errors */}
                    {itemErrors.length > 0 && (
                      <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-amber-800 mb-1">Issues with this item:</p>
                        <ul className="space-y-0.5">
                          {itemErrors.map((err, i) => (
                            <li key={i} className="text-xs text-amber-700">
                              {err.replace(`Item ${index + 1}: `, '')}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Global validation summary */}
      {errors.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-start">
            <div className="w-5 h-5 text-amber-600 mr-2 mt-0.5">
              <svg fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-amber-800 mb-1">Please fix the issues above before saving.</h3>
            </div>
          </div>
        </div>
      )}

      {/* Activity-level Image-to-Sentences Popup */}
      {showSentenceImagePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Generate Sentences from Image</h3>
                <p className="text-sm text-gray-600 mt-1">Upload an image of your worksheet to create multiple fill-in-the-blank sentences.</p>
              </div>
              <button
                type="button"
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={closeSentenceImagePopup}
              >
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Upload / Preview */}
              <div>
                {!sentenceUploadedImage ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-emerald-400 transition-colors">
                    <FaCamera className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-700 mb-2 font-medium">Upload a worksheet image</p>
                    <p className="text-xs text-gray-500 mb-4">Supported formats: JPG, PNG. Lines of text will be converted to sentences.</p>
                    <input
                      type="file"
                      id="sentenceImageUpload"
                      className="hidden"
                      accept="image/*"
                      onChange={handleSentenceImageUpload}
                      disabled={sentenceImageProcessing}
                    />
                    <button
                      type="button"
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium disabled:bg-emerald-400 disabled:cursor-not-allowed"
                      onClick={() => document.getElementById('sentenceImageUpload').click()}
                      disabled={sentenceImageProcessing}
                    >
                      Choose Image File
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      {sentenceImageBlobUrl && (
                        <img
                          src={sentenceImageBlobUrl}
                          alt="Uploaded worksheet"
                          className="w-full h-auto rounded-lg shadow-sm"
                          style={{ maxHeight: '260px', objectFit: 'contain' }}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                      onClick={() => {
                        setSentenceUploadedImage(null);
                        if (sentenceImageBlobUrl) {
                          URL.revokeObjectURL(sentenceImageBlobUrl);
                        }
                        setSentenceImageBlobUrl(null);
                        setGeneratedSentences([]);
                      }}
                    >
                      Choose Different Image
                    </button>
                  </div>
                )}
              </div>

              {/* Generated Sentences */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Generated Sentences</h4>
                {sentenceImageProcessing ? (
                  <div className="flex items-center justify-center h-40 border border-gray-200 rounded-lg bg-gray-50">
                    <FaSpinner className="w-6 h-6 animate-spin text-emerald-600 mr-2" />
                    <span className="text-sm text-gray-700">Extracting sentences from image...</span>
                  </div>
                ) : generatedSentences.length === 0 ? (
                  <p className="text-xs text-gray-500 border border-dashed border-gray-200 rounded-lg p-3 bg-gray-50">
                    After uploading an image, each detected sentence/line will appear here. You can edit them and later add blanks (_______).
                  </p>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500 mb-1">Review and edit the sentences below. You can insert ______ where the blank should appear after they are added.</p>
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                      {generatedSentences.map((sentence, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <span className="w-6 text-xs text-gray-500 mt-1">{idx + 1}.</span>
                          <textarea
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500"
                            rows={2}
                            value={sentence}
                            onChange={(e) => {
                              const updated = [...generatedSentences];
                              updated[idx] = e.target.value;
                              setGeneratedSentences(updated);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
                      onClick={applyGeneratedSentences}
                      disabled={generatedSentences.length === 0}
                    >
                      Add {generatedSentences.length} Sentences to Activity
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image-to-options Popup */}
      {showImagePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Generate Options from Image</h3>
                <p className="text-sm text-gray-600 mt-1">Upload an image containing words or short phrases to use as options.</p>
              </div>
              <button
                type="button"
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                onClick={closeImagePopup}
              >
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Upload / Preview */}
              <div>
                {!uploadedImage ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                    <FaCamera className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-sm text-gray-700 mb-2 font-medium">Upload an image to extract options</p>
                    <p className="text-xs text-gray-500 mb-4">Supported formats: JPG, PNG. Keep text clear and high contrast.</p>
                    <input
                      type="file"
                      id="optionsImageUpload"
                      className="hidden"
                      accept="image/*"
                      onChange={handleImageUploadForOptions}
                      disabled={imageProcessing}
                    />
                    <button
                      type="button"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:bg-blue-400 disabled:cursor-not-allowed"
                      onClick={() => document.getElementById('optionsImageUpload').click()}
                      disabled={imageProcessing}
                    >
                      Choose Image File
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                      {imageBlobUrl && (
                        <img
                          src={imageBlobUrl}
                          alt="Uploaded for options"
                          className="w-full h-auto rounded-lg shadow-sm"
                          style={{ maxHeight: '260px', objectFit: 'contain' }}
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                      onClick={() => {
                        setUploadedImage(null);
                        if (imageBlobUrl) {
                          URL.revokeObjectURL(imageBlobUrl);
                        }
                        setImageBlobUrl(null);
                        setGeneratedOptions([]);
                      }}
                    >
                      Choose Different Image
                    </button>
                  </div>
                )}
              </div>

              {/* Generated options */}
              <div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2">Suggested Options</h4>
                {imageProcessing ? (
                  <div className="flex items-center justify-center h-40 border border-gray-200 rounded-lg bg-gray-50">
                    <FaSpinner className="w-6 h-6 animate-spin text-blue-600 mr-2" />
                    <span className="text-sm text-gray-700">Extracting text from image...</span>
                  </div>
                ) : generatedOptions.length === 0 ? (
                  <p className="text-xs text-gray-500 border border-dashed border-gray-200 rounded-lg p-3 bg-gray-50">
                    After uploading an image, recognized words and phrases will appear here. You can edit them before applying.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-gray-500 mb-1">Edit the suggestions below. The first four will be applied as options (Option 1-4).</p>
                    <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                      {generatedOptions.map((opt, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <span className="w-6 text-xs text-gray-500">{idx + 1}.</span>
                          <input
                            type="text"
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            value={opt}
                            onChange={(e) => {
                              const updated = [...generatedOptions];
                              updated[idx] = e.target.value;
                              setGeneratedOptions(updated);
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="w-full px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm font-medium"
                      onClick={applyGeneratedOptionsToItem}
                      disabled={generatedOptions.length === 0}
                    >
                      Use Top 4 as Options for Item {targetItemIndex != null ? targetItemIndex + 1 : ''}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Standalone validator used by admin registry
export const validateFillInBlankMcqContent = (content) => {
  const errors = [];

  if (!content || typeof content !== 'object') {
    errors.push('Content must be an object');
    return errors;
  }

  if (!Array.isArray(content.items)) {
    errors.push('Content must have an items array');
    return errors;
  }

  if (content.items.length === 0) {
    errors.push('At least one sentence is required');
    return errors;
  }

  content.items.forEach((item, index) => {
    const prefix = `Item ${index + 1}:`;

    if (!item.sentence || !item.sentence.trim()) {
      errors.push(`${prefix} Sentence is required`);
    }

    if (!item.options || !Array.isArray(item.options) || item.options.length === 0) {
      errors.push(`${prefix} At least one option is required`);
    } else {
      const filledOptions = item.options.filter(opt => opt && opt.trim());
      if (filledOptions.length < 2) {
        errors.push(`${prefix} At least two non-empty options are required`);
      }

      if (!item.correctOption || !filledOptions.includes(item.correctOption)) {
        errors.push(`${prefix} Correct answer must be one of the non-empty options`);
      }
    }
  });

  return errors;
};

export default FillInBlankMcqActivity;
